<template>
    <UiModal
        :is-open="!!store.activeModal"
        :title="modalTitle"
        width="560px"
        @close="store.closeModal()"
    >
        <template #title>
            <div class="flex items-center gap-2.5">
                <h5 class="ui-modal-title">{{ modalTitle }}</h5>
                <span
                    v-if="itemCount > 0"
                    class="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                >
                    {{ itemCount }}
                </span>
            </div>
        </template>

        <template #content>
            <!-- Loading -->
            <div v-if="store.loading || benefitsLoading" class="py-12">
                <LoadingInline :loading="true" />
            </div>

            <!-- Annual Fees -->
            <template v-else-if="store.activeModal === 'annual-fees'">
                <div v-if="store.annualFees.length" class="divide-y divide-slate-100 dark:divide-slate-700">
                    <div
                        v-for="account in store.annualFees"
                        :key="account.id"
                        class="flex items-center gap-4 px-5 py-4"
                    >
                        <div class="shrink-0 w-10 h-7 flex items-center justify-center">
                            <img
                                v-if="account.card?.card_image"
                                :src="`${cdnUrl}/${account.card.card_image.path}/_small/${account.card.card_image.file_name}`"
                                :alt="account.card?.title"
                                class="max-h-7 max-w-[2.5rem] object-contain"
                            />
                            <div v-else class="w-10 h-7 rounded bg-slate-100" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                                {{ account.card?.title ?? account.name }}
                            </p>
                            <p class="text-xs text-slate-500 dark:text-slate-400">
                                {{ account.last_four ? `···· ${account.last_four}` : 'No last four on file' }}
                            </p>
                        </div>
                        <div class="text-right shrink-0">
                            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                ${{ account.annual_fee > 0 ? account.annual_fee : account.card?.annual_fee }}
                            </p>
                            <p class="text-xs text-slate-500 dark:text-slate-400">
                                Due {{ account.annual_fee_due_date?.date }}
                            </p>
                        </div>
                    </div>
                </div>
                <EmptyState
                    v-else
                    icon="circle-check"
                    title="No annual fees due"
                    description="You don't have any annual fees due in the next 30 days."
                    :hasAction="false"
                />
            </template>

            <!-- Min Spend Deadlines -->
            <template v-else-if="store.activeModal === 'min-spend'">
                <div v-if="store.minSpendDeadlines.length" class="divide-y divide-slate-100 dark:divide-slate-700">
                    <div
                        v-for="account in store.minSpendDeadlines"
                        :key="account.id"
                        class="flex items-start gap-4 px-5 py-4"
                    >
                        <div class="shrink-0 w-10 h-7 flex items-center justify-center mt-0.5">
                            <img
                                v-if="account.card?.card_image"
                                :src="`${cdnUrl}/${account.card.card_image.path}/_small/${account.card.card_image.file_name}`"
                                :alt="account.card?.title"
                                class="max-h-7 max-w-[2.5rem] object-contain"
                            />
                            <div v-else class="w-10 h-7 rounded bg-slate-100" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                                {{ account.card?.title ?? account.name }}
                            </p>
                            <p class="text-xs text-slate-500 dark:text-slate-400">
                                Deadline: {{ formatDate(account.welcome_bonus?.minimum_spend_deadline) }}
                                &nbsp;·&nbsp;
                                {{ daysUntil(account.welcome_bonus?.minimum_spend_deadline) }} days left
                            </p>
                        </div>
                        <div class="shrink-0 text-right">
                            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                {{ formatBonus(account.welcome_bonus?.bonus_amount) }} pts
                            </p>
                            <p class="text-xs text-slate-500 dark:text-slate-400">
                                after ${{ formatAmount(account.welcome_bonus?.minimum_spend_amount) }} spend
                            </p>
                        </div>
                    </div>
                </div>
                <EmptyState
                    v-else
                    icon="circle-check"
                    title="No minimum spend deadlines"
                    description="You don't have any upcoming minimum spend deadlines."
                    :hasAction="false"
                />
            </template>

            <!-- Expiring Benefits -->
            <template v-else-if="store.activeModal === 'expiring-benefits'">
                <div v-if="store.expiringBenefits.length" class="divide-y divide-slate-100 dark:divide-slate-700">
                    <div
                        v-for="item in store.expiringBenefits"
                        :key="`${item.connected_account_id}-${item.benefit?.card_benefit_id}`"
                        class="flex items-start gap-4 px-5 py-4"
                    >
                        <div class="shrink-0 w-10 h-7 flex items-center justify-center mt-0.5">
                            <img
                                v-if="item.card?.image"
                                :src="`${cdnUrl}/${item.card.image.path}/_small/${item.card.image.file_name}`"
                                :alt="item.card?.title"
                                class="max-h-7 max-w-[2.5rem] object-contain"
                            />
                            <div v-else class="w-10 h-7 rounded bg-slate-100" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="flex items-start justify-between gap-3">
                                <div class="min-w-0">
                                    <p class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                                        {{ item.benefit?.benefit_title }}
                                    </p>
                                    <p class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ item.card?.title }}</p>
                                </div>
                                <div class="shrink-0 flex flex-col items-end gap-1">
                                    <span class="text-sm text-slate-800 dark:text-slate-100 font-normal">
                                        {{ benefitsStore.getExpirationTime(item.period_end_date, item.benefit?.period_type) }}
                                    </span>
                                    <span class="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                        <Icon icon="arrows-repeat" class="h-3.5 w-3.5" />
                                        {{ benefitsStore.formatPeriodType(item.benefit?.period_type) }}
                                    </span>
                                    <div class="inline-flex items-center px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        {{ filters.formatSmartMoney(benefitsStore.calculateDisplayAmount(item)) }}/{{ benefitsStore.getPeriodTypeFormat(item.benefit?.period_type, 'noun') }}
                                    </div>
                                </div>
                            </div>
                            <div class="mt-2">
                                <div class="w-full bg-orange-200 rounded-full h-1">
                                    <div
                                        class="bg-10x-orange h-1 rounded-full transition-all"
                                        :style="{ width: `${usagePercent(item)}%` }"
                                    />
                                </div>
                                <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {{ filters.formatSmartMoney(item.current_period_usage || 0) }} of {{ filters.formatSmartMoney(benefitsStore.calculateDisplayAmount(item)) }} used this {{ benefitsStore.getPeriodTypeFormat(item.benefit?.period_type, 'noun') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <EmptyState
                    v-else
                    icon="coin"
                    title="No benefits expiring soon"
                    description="You don't have any expiring card benefits to act on."
                    :hasAction="false"
                />
            </template>

            <!-- Welcome Bonus Past Deadline -->
            <template v-else-if="store.activeModal === 'welcome-bonus'">
                <div v-if="store.welcomeBonusPastDeadline.length" class="divide-y divide-slate-100 dark:divide-slate-700">
                    <a
                        v-for="account in store.welcomeBonusPastDeadline"
                        :key="account.id"
                        :href="`/wallet?card_id=${account.id}&open_sidebar=true`"
                        class="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        @click="store.closeModal()"
                    >
                        <div class="shrink-0 w-10 h-7 flex items-center justify-center mt-0.5">
                            <img
                                v-if="account.card?.card_image"
                                :src="`${cdnUrl}/${account.card.card_image.path}/_small/${account.card.card_image.file_name}`"
                                :alt="account.card?.title"
                                class="max-h-7 max-w-[2.5rem] object-contain"
                            />
                            <div v-else class="w-10 h-7 rounded bg-slate-100" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                                {{ account.card?.title ?? account.name }}
                            </p>
                            <p class="text-xs text-slate-500 dark:text-slate-400">
                                Deadline passed: {{ formatDate(account.welcome_bonus?.minimum_spend_deadline) }}
                            </p>
                        </div>
                        <div class="shrink-0 text-right">
                            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                {{ formatBonus(account.welcome_bonus?.bonus_amount) }} pts
                            </p>
                            <p class="text-xs text-slate-500 dark:text-slate-400">
                                after ${{ formatAmount(account.welcome_bonus?.minimum_spend_amount) }} spend
                            </p>
                        </div>
                    </a>
                </div>
                <EmptyState
                    v-else
                    icon="circle-check"
                    title="No bonuses to update"
                    description="You don't have any welcome bonuses with a passed deadline to review."
                    :hasAction="false"
                />
            </template>

            <!-- Unmatched Cards -->
            <template v-else-if="store.activeModal === 'unmatched-cards'">
                <div v-if="store.unmatchedCards.length" class="divide-y divide-slate-100 dark:divide-slate-700">
                    <div
                        v-for="account in store.unmatchedCards"
                        :key="account.id"
                        class="flex items-center gap-4 px-5 py-4"
                    >
                        <div class="shrink-0 w-10 h-7 flex items-center justify-center">
                            <img
                                v-if="account.institution?.small_logo_url"
                                :src="account.institution.small_logo_url"
                                :alt="account.institution?.name"
                                class="max-h-7 max-w-[2.5rem] object-contain"
                            />
                            <div v-else class="w-10 h-7 rounded bg-slate-100" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                                {{ account.card_not_found_name ?? account.name }}
                            </p>
                            <p class="text-xs text-slate-500 dark:text-slate-400">
                                {{ account.institution?.name ?? 'Unknown bank' }}
                                <span v-if="account.last_four"> · ···· {{ account.last_four }}</span>
                            </p>
                        </div>
                        <span class="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                            Needs review
                        </span>
                    </div>
                </div>
                <EmptyState
                    v-else
                    icon="circle-check"
                    title="All cards are matched"
                    description="There are no unmatched cards to review."
                    :hasAction="false"
                />
            </template>
        </template>

        <template #footer>
            <div class="flex items-center justify-end gap-3">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="store.closeModal()"
                >
                    Close
                </button>
                <a
                    :href="footerLink"
                    class="btn btn-primary"
                    @click="store.closeModal()"
                >
                    {{ footerLabel }}
                </a>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import LoadingInline from '@/components/ui/loading-inline.vue';
import Icon from '@/components/ui/icon/icon.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import { useDecisionsModalStore } from '@/store/decisions-modal';
import { useCardBenefitsStore } from '@/store/card-benefits';
import filters from '@/filters';

const store = useDecisionsModalStore();
const benefitsStore = useCardBenefitsStore();
const { loading: benefitsLoading } = storeToRefs(benefitsStore);

const cdnUrl = window.cdnUrl || '';

const modalConfig = {
    'annual-fees': {
        title: 'Upcoming Annual Fees',
        footerLabel: 'View wallet',
        footerLink: '/wallet',
    },
    'min-spend': {
        title: 'Minimum Spend Deadlines',
        footerLabel: 'View wallet',
        footerLink: '/wallet',
    },
    'expiring-benefits': {
        title: 'Expiring Benefits',
        footerLabel: 'View all benefits',
        footerLink: '/card-benefits',
    },
    'unmatched-cards': {
        title: 'Unmatched Cards',
        footerLabel: 'View wallet',
        footerLink: '/wallet',
    },
    'welcome-bonus': {
        title: 'Did You Meet Your Welcome Bonus?',
        footerLabel: 'View wallet',
        footerLink: '/wallet',
    },
};

const modalTitle = computed(() => modalConfig[store.activeModal]?.title ?? '');
const footerLabel = computed(() => modalConfig[store.activeModal]?.footerLabel ?? '');
const footerLink = computed(() => modalConfig[store.activeModal]?.footerLink ?? '/wallet');

const itemCount = computed(() => {
    switch (store.activeModal) {
        case 'annual-fees': return store.annualFees.length;
        case 'min-spend': return store.minSpendDeadlines.length;
        case 'expiring-benefits': return store.expiringBenefits.length;
        case 'unmatched-cards': return store.unmatchedCards.length;
        case 'welcome-bonus': return store.welcomeBonusPastDeadline.length;
        default: return 0;
    }
});

function formatDate(dateString) {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(dateString) {
    if (!dateString) return 0;
    return Math.max(0, Math.ceil((new Date(dateString) - new Date()) / (1000 * 60 * 60 * 24)));
}

function formatAmount(value) {
    if (!value) return '0';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatBonus(value) {
    if (!value) return '—';
    return Number(value).toLocaleString('en-US');
}

function usagePercent(item) {
    const used = parseFloat(item.current_period_usage || 0);
    const total = parseFloat(benefitsStore.calculateDisplayAmount(item) || 0);
    if (!total) return 0;
    return Math.min(100, Math.max(0, (used / total) * 100));
}
</script>
